import type { OrderFact } from '../api';

interface OrdersTableProps {
  orders: OrderFact[];
  isLoading?: boolean;
  onOrderClick: (order: OrderFact) => void;
  emptyMessage?: string;
}

function formatCurrency(amount: number | string, currency = 'USD') {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(num);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'verified':
      return 'bg-emerald-500/20 text-emerald-400';
    case 'unverified':
      return 'bg-amber-500/20 text-amber-400';
    case 'needs_review':
      return 'bg-orange-500/20 text-orange-400';
    case 'unattributed':
      return 'bg-ink-700/50 text-ink-400';
    default:
      return 'bg-ink-700/50 text-ink-400';
  }
}

export default function OrdersTable({
  orders,
  isLoading,
  onOrderClick,
  emptyMessage = 'No orders in this time range',
}: OrdersTableProps) {
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="w-8 h-8 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="p-8 text-center text-ink-400">{emptyMessage}</div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left text-ink-400 text-sm border-b border-ink-800/50">
            <th className="px-4 py-3 font-medium">Order ID</th>
            <th className="px-4 py-3 font-medium">Date</th>
            <th className="px-4 py-3 font-medium">Customer</th>
            <th className="px-4 py-3 font-medium">Location</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const customerName = [order.customer_first_name, order.customer_last_name]
              .filter(Boolean)
              .join(' ');
            const location = [order.order_state || order.shipping_province, order.order_country]
              .filter(Boolean)
              .join(', ');

            return (
              <tr
                key={order.id}
                onClick={() => onOrderClick(order)}
                className="border-b border-ink-800/30 hover:bg-ink-800/30 cursor-pointer transition-colors group"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOrderClick(order);
                  }
                }}
                role="button"
                aria-label={`View order ${order.shopify_order_id}`}
              >
                <td className="px-4 py-4">
                  <code className="text-sm font-mono text-ink-200 group-hover:text-accent-400 transition-colors">
                    #{order.shopify_order_id}
                  </code>
                </td>
                <td className="px-4 py-4 text-ink-300">
                  {formatDate(order.created_at)}
                </td>
                <td className="px-4 py-4 text-ink-300">
                  {customerName || <span className="text-ink-500">—</span>}
                </td>
                <td className="px-4 py-4">
                  {location ? (
                    <span className="text-ink-300">{location}</span>
                  ) : (
                    <span className="text-ink-500">—</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(order.attribution_status)}`}>
                    {order.attribution_status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-4 text-right font-medium text-ink-100">
                  {formatCurrency(order.total_price, order.currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
